// Bar tab: POS (sell) · Stock · Today (tickets, summary, what to order).
import React, { useCallback, useEffect, useState } from 'react';
import { ShoppingCart, Boxes, ReceiptText, BellRing, Umbrella } from 'lucide-react';
import { apiCall } from '../_shared';
import BarPos from './BarPos';
import BarStock from './BarStock';
import BarDay from './BarDay';
import BarOrders from './BarOrders';
import TerraceLayout from './TerraceLayout';

export const CAT_LABEL = { coffee: 'Coffee', tea: 'Tea', soft_drinks: 'Soft drinks', water: 'Water', juices: 'Juices', snacks: 'Snacks', ice_cream: 'Ice cream', other: 'Other' };
export const money = (v) => `€${Number(v || 0).toFixed(2)}`;

export const STOCK_ROLES = ['super_admin', 'manager', 'accountant'];

const VIEWS = [
  { id: 'pos', label: 'Sell', icon: ShoppingCart },
  { id: 'orders', label: 'Orders', icon: BellRing },
  { id: 'terrace', label: 'Terrace', icon: Umbrella },
  { id: 'stock', label: 'Stock', icon: Boxes },
  { id: 'day', label: 'Today', icon: ReceiptText },
];
const VIEWS_BY_ROLE = { waiter: ['pos', 'orders'], accountant: ['stock'] };

const BarTab = ({ userRole }) => {
  const views = VIEWS.filter((v) => !VIEWS_BY_ROLE[userRole] || VIEWS_BY_ROLE[userRole].includes(v.id));
  const [view, setView] = useState(() => { const v = new URLSearchParams(window.location.search).get('view'); return views.some((x) => x.id === v) ? v : views[0].id; });
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [newOrders, setNewOrders] = useState(0);
  useEffect(() => {
    const poll = () => apiCall('/api/admin/inroom/orders?status=new&limit=1').then((r) => setNewOrders(r.counts.new)).catch(() => {});
    poll(); const t = setInterval(poll, 20000); return () => clearInterval(t);
  }, []);
  const load = useCallback(async () => {
    try { setData(await apiCall('/api/admin/bar/products?include_inactive=true')); setErr(''); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const canManage = ['super_admin', 'admin', 'manager'].includes(userRole);
  const canStock = STOCK_ROLES.includes(userRole);
  const lowCount = data?.low_stock?.length || 0;
  const labels = { ...CAT_LABEL, ...Object.fromEntries((data?.category_meta || []).map((c) => [c.id, c.label])) };
  const tabs = views.map((v) => (
    <button key={v.id} type="button" onClick={() => setView(v.id)} data-testid={`bar-view-${v.id}`}
      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${view === v.id ? 'bg-slate-900 text-white' : 'bg-white border text-slate-700 hover:bg-slate-50'}`}>
      <v.icon className="w-4 h-4" /> {v.label}{v.id === 'stock' && lowCount > 0 && <span className="ml-1 bg-amber-500 text-white text-[10px] rounded-full px-1.5" data-testid="bar-low-badge">{lowCount}</span>}{v.id === 'orders' && newOrders > 0 && <span className="ml-1 bg-orange-500 text-white text-[10px] rounded-full px-1.5 animate-pulse" data-testid="bar-orders-badge">{newOrders}</span>}
    </button>
  ));
  return (
    <div className="space-y-4" data-testid="bar-tab">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-xl font-bold text-slate-900 mr-2">Bar</h2>
        {tabs}
        {data && <span className="ml-auto text-xs text-slate-500" data-testid="bar-stock-value">Stock value {money(data.stock_value)}</span>}
      </div>
      {err && <p className="text-sm text-red-600" data-testid="bar-error">{err}</p>}
      {data && view === 'pos' && <BarPos products={data.products.filter((p) => p.active)} labels={labels} onSold={load} />}
      {data && view === 'stock' && <BarStock data={data} labels={labels} reload={load} canManage={canManage} canStock={canStock} />}
      {view === 'orders' && <BarOrders onCountChange={setNewOrders} />}
      {view === 'terrace' && <TerraceLayout canManage={canManage} />}
      {view === 'day' && <BarDay canManage={canManage} labels={labels} onChanged={load} />}
    </div>
  );
};

export default BarTab;
